import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type ApiEnvelope } from '@/lib/api/client';

/**
 * Item Master — the catalogue item itself, above any one store.
 *
 * An *item* is what a product is: its names, its pictures, its barcodes, what
 * it is made of. A *business item* is what one store says about stocking it —
 * price, availability, aisle. This module is the first half; the second is
 * reached from an item's list of stores.
 *
 * Every shape here is from docs/grocefy-api.swagger.json. Two things to hold
 * on to, because they are not guessable:
 *
 *   - list endpoints answer `data.results`, but `businesses/gtins/{itemId}`
 *     answers `data` as a bare array;
 *   - media is one list with a `type` (0 picture, 1 video), not two lists.
 */

const get = <T>(url: string) => unwrap<T>(api.get<ApiEnvelope<T>>(url));

/** `MediaType`. */
export const MEDIA_TYPE = { picture: 0, video: 1 } as const;

/** `ItemMediaDto`. */
export interface ItemMedia {
  id: string;
  url: string | null;
  thumbnailUrl: string | null;
  type: number;
  itemId: string | null;
  ordering: number | null;
  isDeleted: boolean;
  createdAt: string | null;
}

/** `ItemRecommendationDto` and `ItemSubstituteDto` — identical shapes. */
export interface ItemRelation {
  id: string;
  itemId: string | null;
  parentId: string | null;
  itemImage: string | null;
  brand: string | null;
  name: string | null;
  description: string | null;
  ordering: number | null;
  createdAt: string | null;
}

/** `ItemDto`. */
export interface MasterItem {
  id: string;
  gtin: string | null;
  brand: string | null;
  name: string | null;
  nameSpa: string | null;
  description: string | null;
  descriptionSpa: string | null;
  itemImage: string | null;
  itemThumbnail: string | null;
  searchTags: string | null;
  internalId: string | null;
  internalRank: number | null;
  suggestedDepartment: string | null;
  isDeleted: boolean;
  isPackagingSensitive: boolean;
  isTemperatureSensitive: boolean;
  isAlcoholicBeverage: boolean;
  isPerishable: boolean;
  isEbt: boolean;
  idRequired: boolean;
  hasPluCode: boolean;
  createdAt: string | null;
  editedAt: string | null;
  itemMedias: ItemMedia[] | null;
  itemRecommendations: ItemRelation[] | null;
  /** Plain strings here, unlike the `gtins/{itemId}` endpoint's objects. */
  gtins: string[] | null;
}

/**
 * `ItemMasterBindingModel` — what the update takes.
 *
 * Note what it is *not*: it has no `id` (that is in the path), it adds
 * `affectsInventory` and `businessIds`, and it carries none of the read-only
 * fields the DTO returns. Posting the DTO back would send seven fields the
 * model does not have and miss two it does.
 */
export interface MasterItemInput {
  gtin: string | null;
  brand: string | null;
  name: string | null;
  description: string | null;
  nameSpa: string | null;
  descriptionSpa: string | null;
  itemImage: string | null;
  itemThumbnail: string | null;
  searchTags: string | null;
  isDeleted: boolean;
  isPackagingSensitive: boolean;
  isTemperatureSensitive: boolean;
  isAlcoholicBeverage: boolean;
  isPerishable: boolean;
  isEbt: boolean;
  idRequired: boolean;
  affectsInventory: boolean;
  internalId: string | null;
  suggestedDepartment: string | null;
  internalRank: number | null;
  hasPluCode: boolean;
  businessIds: string[];
}

export function toItemInput(item: MasterItem): MasterItemInput {
  return {
    gtin: item.gtin,
    brand: item.brand,
    name: item.name,
    description: item.description,
    nameSpa: item.nameSpa,
    descriptionSpa: item.descriptionSpa,
    itemImage: item.itemImage,
    itemThumbnail: item.itemThumbnail,
    searchTags: item.searchTags,
    isDeleted: item.isDeleted,
    isPackagingSensitive: item.isPackagingSensitive,
    isTemperatureSensitive: item.isTemperatureSensitive,
    isAlcoholicBeverage: item.isAlcoholicBeverage,
    isPerishable: item.isPerishable,
    isEbt: item.isEbt,
    idRequired: item.idRequired,
    affectsInventory: false,
    internalId: item.internalId,
    suggestedDepartment: item.suggestedDepartment,
    internalRank: item.internalRank,
    hasPluCode: item.hasPluCode,
    businessIds: [],
  };
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['item-master'] });
    void queryClient.invalidateQueries({ queryKey: ['catalog-search'] });
  };
}

// ------------------------------------------------------------- the item

export function useUpdateItem() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: MasterItemInput }) =>
      unwrap<MasterItem>(api.post(`businesses/catalog/${id}/update`, input)),
    onSuccess: invalidate,
  });
}

/** `businesses/catalog/new` — the create form's own endpoint. */
export function useCreateItem() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (input: MasterItemInput) =>
      unwrap<MasterItem>(api.post('businesses/catalog/new', input)),
    onSuccess: invalidate,
  });
}

export function useArchiveItem() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: string) =>
      unwrap<unknown>(api.post(`businesses/catalog/archive/${id}`, {})),
    onSuccess: invalidate,
  });
}

export function useRestoreItem() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: string) =>
      unwrap<unknown>(api.post(`businesses/catalog/archive/${id}/revert`, {})),
    onSuccess: invalidate,
  });
}

/** The divisions an item can be suggested into. */
export function useDivisions() {
  return useQuery({
    queryKey: ['item-master', 'divisions'],
    queryFn: () => get<{ results: string[] }>('businesses/divisions'),
    select: (data) => data.results ?? [],
    staleTime: 10 * 60 * 1000,
  });
}

// ----------------------------------------------------------- the stores

/** `ItemPriceDto` — one store's price row, from `BusinessItemDto.prices`. */
export interface BusinessItemPrice {
  id: string;
  taxRule: number;
  ordering: number;
  basePriceMethod: number;
  basePrice: number;
  baseMultiple: number;
  promoPrice: number;
  promoMultiple: number;
  promoPriceMethod: number;
  promoStartDate: string | null;
  promoEndDate: string | null;
  cost: number;
  costOfBox: number;
  pack: number;
  name: string | null;
  description: string | null;
  activePrice: number | null;
  regularPrice: number | null;
  promotionalPrice: number | null;
  hasActivePromotion: boolean;
  isDeleted: boolean;
  isActive: boolean;
}

/** One entry of an update's `prices` array: a price model that names its row. */
export interface ItemPriceEntry extends ItemPriceInput {
  /** Absent on a new row. */
  id?: string;
}

/** `ItemPriceBindingModel` — the `price` a business item update carries. */
export interface ItemPriceInput {
  taxRule: number;
  ordering: number;
  basePriceMethod: number;
  basePrice: number;
  baseMultiple: number;
  promoPrice: number;
  promoMultiple: number;
  promoPriceMethod: number;
  promoStartDate: string | null;
  promoEndDate: string | null;
  isDeleted: boolean;
  isActive: boolean;
  cost: number;
  costOfBox: number;
  pack: number;
  name: string | null;
  description: string | null;
}

/** `ItemDataDto` — the dietary and weight facts a store keeps about an item. */
export interface BusinessItemData {
  isAllNatural: boolean;
  isLactoseFree: boolean;
  isSugarFree: boolean;
  isKeto: boolean;
  isOrganic: boolean;
  isVegan: boolean;
  isVegetarian: boolean;
  isGlutenFree: boolean;
  isGrassFed: boolean;
  isAntioxidant: boolean;
  isNutFree: boolean;
  isNonGmo: boolean;
  isDeleted: boolean;
  hasRandomWeight: boolean;
  madeLocally: boolean;
  weightType: number;
  minQuantity: number;
  maxQuantity: number;
  stepAmount: number;
  unitPriceFactor: number;
}

/** `ItemTimeConstraintDto` — when in the week the store will sell it. */
export interface BusinessItemTimeConstraint {
  availableOnMondays: boolean;
  availableOnTuesdays: boolean;
  availableOnWednesdays: boolean;
  availableOnThursdays: boolean;
  availableOnFridays: boolean;
  availableOnSaturdays: boolean;
  availableOnSundays: boolean;
  hourDelay: number;
  minuteDelay: number;
  limitDate: string | null;
}

/**
 * `BusinessItemDto` — everything one store says about stocking one item.
 *
 * The price is not a field on the row: a business item carries a *list* of
 * prices, and the live one is the active entry. Reading `price` off the row
 * would have shown nothing at all.
 */
export interface AssignedStore {
  id: string;
  businessId: string | null;
  businessName: string | null;
  itemId: string | null;
  upc: string | null;
  tempName: string | null;
  aisle: string | null;
  department: string | null;
  sectionId: string | null;
  sectionName: string | null;
  quantityOnHand: number | null;
  affectsInventory: boolean;
  allowedInLayaway: boolean;
  hideInKiosk: boolean;
  hideInSelfCheckout: boolean;
  blockPromoPricing: boolean;
  blockOostrigger: boolean;
  isPromoted: boolean;
  margin: number;
  stateTaxPct: number;
  munTaxPct: number;
  customSearchTags: string | null;
  customBooleanProperty01: boolean;
  customBooleanProperty02: boolean;
  customBooleanProperty03: boolean;
  customBooleanProperty04: boolean;
  customBooleanProperty05: boolean;
  customBooleanProperty06: boolean;
  sponsorshipStartDate: string | null;
  sponsorshipEndDate: string | null;
  isVisible: boolean;
  isAvailable: boolean;
  isDeleted: boolean;
  /**
   * `BusinessItemType`. Not in the DTO the spec documents, but the update
   * model takes it and the original read it off the row, so it is optional.
   */
  type?: number | null;
  prices: BusinessItemPrice[] | null;
  datas: BusinessItemData[] | null;
  timeConstraint: BusinessItemTimeConstraint | null;
}

/**
 * `BusinessItemBindingModel` — what `catalog/business/{id}/update` takes.
 *
 * Every field is optional: the old drawer posted `{ IsVisible: false }` on its
 * own to flip one switch. Note the singulars — the DTO answers `prices` and
 * `datas` as lists, and the model takes one `price` and one `data`.
 *
 * `otherBusinessItemIds` is the one field that is not in the swagger: it tells
 * the server to apply the same body to those business items too, which is how
 * one store's configuration is copied onto the rest in a single call.
 */
export interface BusinessItemInput {
  tempName?: string | null;
  department?: string | null;
  aisle?: string | null;
  customSearchTags?: string | null;
  blockPromoPricing?: boolean;
  stateTaxPct?: number;
  munTaxPct?: number;
  margin?: number;
  customBooleanProperty01?: boolean;
  customBooleanProperty02?: boolean;
  customBooleanProperty03?: boolean;
  customBooleanProperty04?: boolean;
  customBooleanProperty05?: boolean;
  customBooleanProperty06?: boolean;
  blockOostrigger?: boolean;
  isPromoted?: boolean;
  isVisible?: boolean;
  isAvailable?: boolean;
  affectsInventory?: boolean;
  sponsorshipStartDate?: string | null;
  sponsorshipEndDate?: string | null;
  hideInKiosk?: boolean;
  hideInSelfCheckout?: boolean;
  allowedInLayaway?: boolean;
  upc?: string | null;
  sectionId?: string | null;
  type?: number | null;
  isDeleted?: boolean;
  /** For a single-price item. Send this or `prices`, never both. */
  price?: ItemPriceInput | null;
  /**
   * For an item with several prices. Wins if `price` is sent too.
   *
   * Each entry is matched to a row by `id`, then by `name`, then by
   * `ordering`, so all three travel together. An entry with none of them is a
   * new row, and `isDeleted` only removes a price from in here — the singular
   * `price` has no way to delete one.
   */
  prices?: ItemPriceEntry[];
  data?: BusinessItemData | null;
  timeConstraint?: BusinessItemTimeConstraint | null;
  otherBusinessItemIds?: string[];
}

/** The price row in force at this store — the active one, else the first. */
export function livePrice(store: AssignedStore): BusinessItemPrice | null {
  return (
    (store.prices ?? []).find((entry) => entry.isActive)
    ?? (store.prices ?? [])[0]
    ?? null
  );
}

/** The price a customer would pay at this store right now. */
export function activePrice(store: AssignedStore) {
  const price = livePrice(store);
  return price?.activePrice ?? price?.regularPrice ?? null;
}

export function useAssignedStores(itemId: string | null) {
  return useQuery({
    queryKey: ['item-master', 'assigned', itemId],
    queryFn: () =>
      get<{ results: AssignedStore[] }>(
        `businesses/catalog/${itemId}/getassigned`
      ),
    select: (data) => data.results ?? [],
    enabled: !!itemId,
  });
}

/** Body is a bare array of business ids — not an object wrapping one. */
export function useAssignToAllStores() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ itemId, businessIds }: { itemId: string; businessIds: string[] }) =>
      unwrap<unknown>(
        api.post(`businesses/catalog/assignall/${itemId}`, businessIds)
      ),
    onSuccess: invalidate,
  });
}

export function useAssignToStore() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ itemId, businessId }: { itemId: string; businessId: string }) =>
      unwrap<unknown>(
        api.post(`businesses/catalog/${businessId}/assign/${itemId}`, {})
      ),
    onSuccess: invalidate,
  });
}

/**
 * Re-pulls this item's prices from the client's price integration.
 *
 * There is no source to choose and no body to send: the integration is the
 * source. Only clients with `hasPriceIntegration` can do this at all, which is
 * how the old drawer gated the same button.
 */
export function useForcePrice() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (itemId: string) =>
      unwrap<unknown>(api.post(`pos/price/force/${itemId}`, {})),
    onSuccess: invalidate,
  });
}

/**
 * The configuration one store keeps for an item, as the update model wants it.
 *
 * The barcode and the menu section travel with it: two stores selling the same
 * item are meant to scan it the same way and shelve it in the same section, so
 * the source is the model for those too.
 *
 * `isDeleted` is the one field held back — that is whether the store stocks
 * the item at all, not how it sells it, and a copy must not un-stock a store.
 *
 * Pricing takes whichever shape the item has: one price goes as `price`,
 * several go as `prices`. Landing this on *another* store is what the array's
 * fallback matching is for — the ids belong to the source, so the target
 * matches each entry by name, then by ordering.
 */
export function toBusinessItemInput(store: AssignedStore): BusinessItemInput {
  const rows = (store.prices ?? []).filter((row) => !row.isDeleted);
  const price = livePrice(store);

  return {
    ...(rows.length > 1
      ? { prices: rows.map(toPriceEntry) }
      : { price: price ? toPriceInput(price) : null }),
    upc: store.upc,
    sectionId: store.sectionId,
    type: store.type,
    tempName: store.tempName,
    department: store.department,
    aisle: store.aisle,
    customSearchTags: store.customSearchTags,
    blockPromoPricing: store.blockPromoPricing,
    stateTaxPct: store.stateTaxPct,
    munTaxPct: store.munTaxPct,
    margin: store.margin,
    customBooleanProperty01: store.customBooleanProperty01,
    customBooleanProperty02: store.customBooleanProperty02,
    customBooleanProperty03: store.customBooleanProperty03,
    customBooleanProperty04: store.customBooleanProperty04,
    customBooleanProperty05: store.customBooleanProperty05,
    customBooleanProperty06: store.customBooleanProperty06,
    blockOostrigger: store.blockOostrigger,
    isPromoted: store.isPromoted,
    isVisible: store.isVisible,
    isAvailable: store.isAvailable,
    affectsInventory: store.affectsInventory,
    sponsorshipStartDate: store.sponsorshipStartDate,
    sponsorshipEndDate: store.sponsorshipEndDate,
    hideInKiosk: store.hideInKiosk,
    hideInSelfCheckout: store.hideInSelfCheckout,
    allowedInLayaway: store.allowedInLayaway,
    data: (store.datas ?? [])[0] ?? null,
    timeConstraint: store.timeConstraint,
  };
}

/** The same, plus the id that names the row it belongs to. */
export function toPriceEntry(price: BusinessItemPrice): ItemPriceEntry {
  return { ...toPriceInput(price), ...(price.id && { id: price.id }) };
}

/** `ItemPriceDto` → `ItemPriceBindingModel`. */
export function toPriceInput(price: BusinessItemPrice): ItemPriceInput {
  return {
    taxRule: price.taxRule,
    ordering: price.ordering,
    basePriceMethod: price.basePriceMethod,
    basePrice: price.basePrice,
    baseMultiple: price.baseMultiple,
    promoPrice: price.promoPrice,
    promoMultiple: price.promoMultiple,
    promoPriceMethod: price.promoPriceMethod,
    promoStartDate: price.promoStartDate,
    promoEndDate: price.promoEndDate,
    isDeleted: false,
    isActive: true,
    cost: price.cost,
    costOfBox: price.costOfBox,
    pack: price.pack,
    name: price.name,
    description: price.description,
  };
}

/**
 * Copies one store's configuration for an item onto other stores.
 *
 * One call, not one per store: the body is posted against the *source* business
 * item, and `otherBusinessItemIds` carries the rest. The old drawer looped over
 * the targets client-side and could half-finish; this cannot.
 */
export function useCopyStoreConfig() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      source,
      targetIds,
    }: {
      source: AssignedStore;
      targetIds: string[];
    }) =>
      unwrap<unknown>(
        api.post(`businesses/catalog/business/${source.id}/update`, {
          ...toBusinessItemInput(source),
          otherBusinessItemIds: targetIds,
        })
      ),
    onSuccess: invalidate,
  });
}

// -------------------------------------------------------------- barcodes

/** `ItemGtinDto`. This endpoint answers a bare array, not `{ results }`. */
export interface ItemGtin {
  id: string;
  itemId: string | null;
  gtin: string;
  isPrimary: boolean;
  createdAt: string | null;
}

export function useItemGtins(itemId: string | null) {
  return useQuery({
    queryKey: ['item-master', 'gtins', itemId],
    queryFn: () => get<ItemGtin[]>(`businesses/gtins/${itemId}`),
    enabled: !!itemId,
  });
}

function useInvalidateGtins(itemId: string | null) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: ['item-master', 'gtins', itemId],
    });
}

export function useCreateItemGtin(itemId: string | null) {
  const invalidate = useInvalidateGtins(itemId);

  return useMutation({
    mutationFn: (gtin: string) =>
      unwrap<unknown>(
        api.post('businesses/gtins/create', { itemId, gtin: gtin.trim() })
      ),
    onSuccess: invalidate,
  });
}

/** Keyed by the barcode's own id, not the item's. */
export function useUpdateItemGtin(itemId: string | null) {
  const invalidate = useInvalidateGtins(itemId);

  return useMutation({
    mutationFn: ({ gtinId, gtin }: { gtinId: string; gtin: string }) =>
      unwrap<unknown>(
        api.post('businesses/gtins/update', { gtinId, gtin: gtin.trim() })
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteItemGtin(itemId: string | null) {
  const invalidate = useInvalidateGtins(itemId);

  return useMutation({
    mutationFn: (gtinId: string) =>
      unwrap<unknown>(api.delete(`businesses/gtins/${gtinId}`)),
    onSuccess: invalidate,
  });
}

export function useSetPrimaryItemGtin(itemId: string | null) {
  const invalidate = useInvalidateGtins(itemId);

  return useMutation({
    mutationFn: (gtinId: string) =>
      unwrap<unknown>(
        api.post(`businesses/gtins/${itemId}/${gtinId}/primary`, {})
      ),
    onSuccess: invalidate,
  });
}

/** Overwrite the barcode a store holds with the item's own. */
export function useForceGtinToStores(itemId: string | null) {
  const invalidate = useInvalidateGtins(itemId);

  return useMutation({
    mutationFn: (businessItemIds: string[]) =>
      unwrap<unknown>(
        api.post('businesses/gtins/forcetostores', { itemId, businessItemIds })
      ),
    onSuccess: invalidate,
  });
}

// ----------------------------------------------------------------- media

export function useItemMedia(itemId: string | null) {
  return useQuery({
    queryKey: ['item-master', 'media', itemId],
    queryFn: () =>
      get<{ results: ItemMedia[] }>(`businesses/catalog/media/${itemId}`),
    select: (data) =>
      [...(data.results ?? [])].sort(
        (a, b) => (a.ordering ?? 0) - (b.ordering ?? 0)
      ),
    enabled: !!itemId,
  });
}

function useInvalidateMedia(itemId: string | null) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: ['item-master', 'media', itemId],
    });
}

export function useAddItemMedia(itemId: string | null) {
  const invalidate = useInvalidateMedia(itemId);

  return useMutation({
    mutationFn: (body: { url: string; thumbnailUrl?: string; type: number }) =>
      unwrap<ItemMedia>(
        api.post('businesses/catalog/media', {
          itemId,
          url: body.url,
          thumbnailUrl: body.thumbnailUrl ?? '',
          type: body.type,
          ordering: 99,
        })
      ),
    onSuccess: invalidate,
  });
}

export function useRemoveItemMedia(itemId: string | null) {
  const invalidate = useInvalidateMedia(itemId);

  return useMutation({
    mutationFn: (mediaId: string) =>
      unwrap<unknown>(
        api.post(`businesses/catalog/removemedia/${mediaId}`, {})
      ),
    onSuccess: invalidate,
  });
}

export function useReorderItemMedia(itemId: string | null) {
  const invalidate = useInvalidateMedia(itemId);

  return useMutation({
    mutationFn: (media: ItemMedia[]) =>
      Promise.all(
        media.map((entry, index) =>
          unwrap<unknown>(
            api.post(`businesses/catalog/media/${entry.id}`, {
              itemId,
              url: entry.url,
              thumbnailUrl: entry.thumbnailUrl ?? '',
              type: entry.type,
              ordering: index,
            })
          )
        )
      ),
    onSuccess: invalidate,
  });
}

// --------------------------------------------------------------- filters

/** `FilterDto`. `count` is how many items carry it. */
export interface CatalogFilter {
  id: string;
  name: string | null;
  nameSpa: string | null;
  imageUrl: string | null;
  ordering: number | null;
  count: number | null;
  isDeleted: boolean;
  createdAt: string | null;
}

export type FilterInput = Pick<
  CatalogFilter,
  'name' | 'nameSpa' | 'imageUrl' | 'ordering'
>;

export function useCatalogFilters() {
  return useQuery({
    queryKey: ['item-master', 'filters'],
    queryFn: () => get<{ results: CatalogFilter[] }>('businesses/catalog/filters'),
    select: (data) =>
      [...(data.results ?? [])].sort(
        (a, b) => (a.ordering ?? 0) - (b.ordering ?? 0)
      ),
  });
}

function useInvalidateFilters() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['item-master', 'filters'] });
    void queryClient.invalidateQueries({ queryKey: ['catalog-search'] });
  };
}

export function useSaveFilter() {
  const invalidate = useInvalidateFilters();

  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: FilterInput }) =>
      unwrap<unknown>(
        api.post(
          id ? `businesses/catalog/filters/${id}` : 'businesses/catalog/filters',
          input
        )
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteFilter() {
  const invalidate = useInvalidateFilters();

  return useMutation({
    mutationFn: (id: string) =>
      unwrap<unknown>(api.delete(`businesses/catalog/filters/${id}`)),
    onSuccess: invalidate,
  });
}

/** Which filters this one item carries. */
export function useItemFilters(itemId: string | null) {
  return useQuery({
    queryKey: ['item-master', 'item-filters', itemId],
    queryFn: () =>
      get<{ results: CatalogFilter[] }>(`businesses/catalog/${itemId}/filters`),
    select: (data) => data.results ?? [],
    enabled: !!itemId,
  });
}

/** The same call adds and removes — the API toggles. */
export function useToggleItemFilter(itemId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (filterId: string) =>
      unwrap<unknown>(
        api.post(`businesses/catalog/filters/${filterId}/${itemId}`, {})
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['item-master', 'item-filters', itemId],
      }),
  });
}

// ------------------------------------------- recommendations & substitutes

export type RelationKind = 'recommendation' | 'substitute';

/** The two endpoints differ only by a noun — and one of them is misspelled. */
const RELATION_PATHS = {
  recommendation: {
    create: 'businesses/catalog/recomendation',
    list: (id: string) => `businesses/catalog/recomendation/${id}`,
    save: (id: string) => `businesses/catalog/recomendation/${id}`,
    remove: (id: string) => `businesses/catalog/removerecomendation/${id}`,
  },
  substitute: {
    create: 'businesses/catalog/substitutes',
    list: (id: string) => `businesses/catalog/substitutes/${id}`,
    save: (id: string) => `businesses/catalog/substitutes/${id}`,
    remove: (id: string) => `businesses/catalog/removesubstitutes/${id}`,
  },
} as const;

export function useItemRelations(kind: RelationKind, itemId: string | null) {
  return useQuery({
    queryKey: ['item-master', kind, itemId],
    queryFn: () =>
      get<{ results: ItemRelation[] }>(RELATION_PATHS[kind].list(itemId!)),
    select: (data) =>
      [...(data.results ?? [])].sort(
        (a, b) => (a.ordering ?? 0) - (b.ordering ?? 0)
      ),
    enabled: !!itemId,
  });
}

function useInvalidateRelations(kind: RelationKind, itemId: string | null) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: ['item-master', kind, itemId] });
}

/**
 * Add one, or reorder the lot.
 *
 * The save endpoint is keyed by the *relation's* id, so a new one is created by
 * posting against the item it points at — which is why adding and reordering
 * are the same call with different ids.
 */
export function useSaveRelation(kind: RelationKind, itemId: string | null) {
  const invalidate = useInvalidateRelations(kind, itemId);

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { itemId: string; parentId: string; ordering: number };
    }) => unwrap<unknown>(api.post(RELATION_PATHS[kind].save(id), body)),
    onSuccess: invalidate,
  });
}

/**
 * Add a relation.
 *
 * A different path from saving one: the bare noun creates, the noun keyed by a
 * relation id edits. `parentId` is the item being edited and `itemId` is the
 * one being pointed at — the pair reads backwards from the list's own naming,
 * which is why it is spelt out here.
 */
export function useAddRelation(kind: RelationKind, itemId: string | null) {
  const invalidate = useInvalidateRelations(kind, itemId);

  return useMutation({
    mutationFn: (pickedItemId: string) =>
      unwrap<unknown>(
        api.post(RELATION_PATHS[kind].create, {
          parentId: itemId,
          itemId: pickedItemId,
          ordering: 99,
        })
      ),
    onSuccess: invalidate,
  });
}

export function useReorderRelations(kind: RelationKind, itemId: string | null) {
  const invalidate = useInvalidateRelations(kind, itemId);

  return useMutation({
    mutationFn: (relations: ItemRelation[]) =>
      Promise.all(
        relations.map((relation, index) =>
          unwrap<unknown>(
            api.post(RELATION_PATHS[kind].save(relation.id), {
              itemId: relation.itemId,
              parentId: relation.parentId,
              ordering: index,
            })
          )
        )
      ),
    onSuccess: invalidate,
  });
}

export function useRemoveRelation(kind: RelationKind, itemId: string | null) {
  const invalidate = useInvalidateRelations(kind, itemId);

  return useMutation({
    mutationFn: (relationId: string) =>
      unwrap<unknown>(api.post(RELATION_PATHS[kind].remove(relationId), {})),
    onSuccess: invalidate,
  });
}

// ------------------------------------------------------------------ logs

export interface ItemLog {
  id?: string;
  createdAt: string | null;
  message: string | null;
}

export function useItemLogs(itemId: string | null, page: number, size: number) {
  return useQuery({
    queryKey: ['item-master', 'logs', itemId, page, size],
    queryFn: () =>
      get<{ logs: ItemLog[]; count: number }>(
        `admin/itemlogs/${itemId}?page=${page}&pageSize=${size}`
      ),
    enabled: !!itemId,
  });
}

/** Item pictures upload through the generic container uploader. */
export function useUploadItemImage() {
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('file', file);
      return unwrap<string>(
        api.post('utility/itemMedia/uploadfile', body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      );
    },
  });
}
